import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLabels, useFlags } from 'wsm-common-data';
import { hasDetailedIncentivesWidget, isApplicableIncentive } from '../helpers';
import useIncentivesData from '../hooks/useIncentivesData';
import usePaymentData from '../hooks/usePaymentData';
import useTrackEvent from '../hooks/useTrackEvent';
import useUdsLeadGate from '../hooks/useUdsLeadGate';
import SpecialItems from './SpecialsItems';
import SpecialsPlaceholder from './SpecialsPlaceholder';

const SpecialsDisplay = () => {
	const steps = useSelector((state) => state.steps);
	const selectedPaymentType = steps?.payment?.paymentType;
	const [isLoading, hasError, paymentData] = usePaymentData();
	const sendTrackEvent = useTrackEvent();
	const flags = useFlags();
	const enablePreload =
		flags['ws-track-my-offer-preload-payment-data'] || false;
	const isLeadGateActive = useUdsLeadGate();

	const selectedPayment = paymentData?.find(
		(payment) =>
			payment?.paymentLabel?.toLowerCase() === selectedPaymentType
	);

	const [incentivesData, isLoadingIncentives] = useIncentivesData(
		selectedPayment?.incentiveIds?.join('') || ''
	);
	const [appliedIncentives, setAppliedIncentives] = useState([]);
	const labels = useLabels();
	const hasDDCIncentives = hasDetailedIncentivesWidget();

	const isCashTabWithNoIncentives =
		selectedPayment?.paymentLabel?.toLowerCase() === 'cash' &&
		!selectedPayment?.incentiveIds?.length &&
		!appliedIncentives.length;

	// hide selected payment specials if there is payment error
	const hasPaymentError = hasError || selectedPayment?.hasError;

	const shouldDisplayTermsAndIncentives =
		(!hasPaymentError && !isCashTabWithNoIncentives) || hasDDCIncentives;

	const specialsDisplayLoadingStyle = {
		minHeight: '118px'
	};

	useEffect(() => {
		if (incentivesData?.length) {
			const tempIncentives = [];
			incentivesData.forEach((incentive) => {
				if (
					isApplicableIncentive(
						incentive,
						selectedPaymentType,
						selectedPayment
					)
				) {
					tempIncentives.push(incentive);
				}
			});
			setAppliedIncentives(tempIncentives);
		}
	}, [incentivesData, selectedPayment, selectedPaymentType]);

	const openIncentiveHandler = useCallback(() => {
		window.DDC?.pubsub?.publish(
			'wsDetailedIncentives:showAllIncentivesModal'
		);
		sendTrackEvent({
			element: 'All Specials Link',
			elementCTA: labels.get('EXPLORE_MORE_SPECIALS'),
			result: 'Open multi incentives modal'
		});
	}, [sendTrackEvent, labels]);

	// Hide incentives entirely when UDS lead gate is active
	if (isLeadGateActive) return null;

	if (isLoading || isLoadingIncentives) {
		// If preload is enabled, show the specials placeholder with inline loader
		if (enablePreload) {
			return <SpecialsPlaceholder />;
		}

		// If preload is disabled, show skeleton-shimmer
		return (
			<div
				key="specials-display-loading"
				style={specialsDisplayLoadingStyle}
				className="skeleton-shimmer border box box-padding border rounded my-4"
				data-testid="skeleton-shimmer"
			>
				<div className="d-flex flex-row my-2">
					<div className="ddc-font-size-small"> </div>
				</div>
			</div>
		);
	}

	return (
		shouldDisplayTermsAndIncentives && (
			<div
				key="specials-display-incentives"
				className={
					'specials-display-incentives specials-display-react d-flex flex-column border box ' +
					'box-padding border-neutral-0-alpha-3 rounded my-4 bg-contrast-med'
				}
				data-testid="specials-display"
			>
				{!hasPaymentError && (
					<SpecialItems
						key="specials-items"
						incentives={appliedIncentives}
						payment={selectedPayment}
					/>
				)}
				{hasDDCIncentives && (
					<div className="d-flex">
						<i
							className="rounded-circle mr-3 ddc-icon ddc-icon-tag-circle ddc-font-size-large specials-tag text-primary p-0"
							role="presentation"
							aria-hidden="true"
							tabIndex="-1"
						/>
						<button
							className="text-link text-decoration-underline"
							onClick={openIncentiveHandler}
							type="button"
							key="explore-more-specials"
						>
							{labels.get('EXPLORE_MORE_SPECIALS')}
						</button>
					</div>
				)}
			</div>
		)
	);
};

export default SpecialsDisplay;
